import { ReactNode } from 'react';
import { FormattedMessage } from 'react-intl';
import type { InterpolationPreview } from '~/engine/util/algorithms/sdf/ContourOperations';
import { formatMmAsCmAtScale } from '~/utils/units';
import { InterpolationScope } from './interpolationPreviewMessage';

/** Outcome notification for an applied interpolation: what to tell the user. */
export interface InterpolationOutcome {
    severity: 'success' | 'error';
    message: ReactNode;
}

/**
 * Maps the POST-apply re-preview to the outcome notification shown after the
 * user commits an interpolation. Clinically, interpolation is expected to
 * produce a completely filled contour, so the result is always reported
 * explicitly — the filled count on success, and an explicit warning when
 * slices remain unfilled (possible when a structure thinner than the SDF
 * falloff makes a large in-plane jump between slices, so the blend of its
 * interior stays below the iso threshold) or when the result cannot be
 * verified. Pure (no engine runtime imports) so it is unit-testable in
 * isolation.
 *
 * @param plannedSliceCount - Slices the PRE-apply preview said would fill.
 * @param postApplyPreview - Fresh preview computed AFTER the apply ran.
 * @param scope - Which interpolation ran.
 */
export function renderOutcomeMessage(
    plannedSliceCount: number,
    postApplyPreview: InterpolationPreview,
    scope: InterpolationScope
): InterpolationOutcome {
    // Slices still fillable after the apply are precisely the gaps the
    // operation failed to fill. Report WHERE they are and out of how many:
    // "review the contour" with neither is a check the user cannot start on
    // a whole-scan structure, and without the denominator a near-complete
    // fill and a total failure read identically.
    if (postApplyPreview.status === 'fills') {
        const remaining = postApplyPreview.sliceCount;
        return {
            severity: 'error',
            message:
                remaining === 1 ? (
                    <FormattedMessage
                        id="interpolation.result.gapsRemainOne"
                        values={{
                            planned: plannedSliceCount,
                            z: formatMmAsCmAtScale(
                                postApplyPreview.startZMm,
                                'macro'
                            ),
                        }}
                    />
                ) : (
                    <FormattedMessage
                        id="interpolation.result.gapsRemainMany"
                        values={{
                            count: remaining,
                            planned: plannedSliceCount,
                            start: formatMmAsCmAtScale(
                                postApplyPreview.startZMm,
                                'macro'
                            ),
                            end: formatMmAsCmAtScale(
                                postApplyPreview.endZMm,
                                'macro'
                            ),
                        }}
                    />
                ),
        };
    }
    // The expected terminal state differs per scope: a filled current slice
    // re-previews as alreadyContoured; a filled span as nothingToFill.
    const expectedStatus =
        scope === 'current' ? 'alreadyContoured' : 'nothingToFill';
    if (postApplyPreview.status !== expectedStatus) {
        return {
            severity: 'error',
            message: <FormattedMessage id="interpolation.result.unverified" />,
        };
    }
    return {
        severity: 'success',
        message: (
            <FormattedMessage
                id="interpolation.result.filled"
                values={{ count: plannedSliceCount }}
            />
        ),
    };
}
